<template>
  <div ref="watermarkRef" class="watermark-overlay" />
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { useSettingsStore } from '@/stores/settings'
import { useUserStore } from '@/stores/user'
import { getConfigByGroup } from '@/api/system/config'

const props = defineProps({
  /** 模式：'app' 内页（支持变量替换） / 'login' 登录页（纯文字） */
  mode: {
    type: String,
    default: 'app',
    validator: (v) => ['app', 'login'].includes(v)
  }
})

const settingsStore = useSettingsStore()
const watermarkRef = ref(null)

let observer = null
let config = {}

// ========== 模板变量替换（仅 app 模式使用） ==========
function replaceTemplate(template) {
  // login 模式不做变量替换，直接返回原文
  if (props.mode === 'login') return template

  try {
    const userStore = useUserStore()
    const now = new Date()
    const pad = (n) => String(n).padStart(2, '0')

    const vars = {
      '{username}': userStore.displayName || '',
      '{orgName}': userStore.orgDisplayName || '',
      '{userId}': String(userStore.userId || ''),
      '{date}': `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
      '{time}': `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`,
      '{datetime}': `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`,
    }

    let result = template
    for (const [key, value] of Object.entries(vars)) {
      result = result.replace(new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), value)
    }
    return result
  } catch {
    return template
  }
}

// ========== Canvas 绘制水印 ==========
function createWatermark() {
  if (!watermarkRef.value) return

  // 清除旧水印
  watermarkRef.value.style.backgroundImage = ''

  const get = (key, fallback) => config[`watermark.${key}`] ?? config[key] ?? fallback

  // 根据模式读取不同的开关和内容
  const enabledKey = props.mode === 'login' ? 'loginEnabled' : 'enabled'
  const contentKey = props.mode === 'login' ? 'loginContent' : 'content'

  if (get(enabledKey, 'true') === 'false') return

  const text = replaceTemplate(get(contentKey, props.mode === 'login' ? '' : '{username} {date}'))
  if (!text) return // 无内容则不渲染

  const fontSize = parseInt(get('fontSize', '14')) || 14
  const opacity = parseFloat(get('opacity', '0.06')) || 0.06
  const angle = parseFloat(get('angle', '-20')) || -20
  const gap = parseInt(get('gap', '200')) || 200

  // 根据主题选择颜色（登录页固定用深色，因为登录页背景是深色）
  const isDark = settingsStore.resolvedTheme === 'dark' || props.mode === 'login'
  const textColor = isDark
    ? `rgba(255, 255, 255, ${opacity})`
    : `rgba(0, 0, 0, ${opacity})`

  // 创建 Canvas
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  // 计算画布尺寸
  const radian = Math.abs(angle) * Math.PI / 180
  const textWidth = text.length * fontSize + 40
  canvas.width = Math.max(gap, textWidth + gap * Math.sin(radian))
  canvas.height = Math.max(gap, fontSize + 30 + gap * Math.cos(radian))

  // 绘制水印单元
  ctx.font = `${fontSize}px sans-serif`
  ctx.fillStyle = textColor
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'

  // 旋转并绘制
  ctx.translate(canvas.width / 2, canvas.height / 2)
  ctx.rotate(angle * Math.PI / 180)
  ctx.fillText(text, 0, 0)

  // 设置为背景
  const base64 = canvas.toDataURL('image/png')
  watermarkRef.value.style.backgroundImage = `url(${base64})`
}

// ========== 加载配置 ==========
async function loadConfig() {
  try {
    const res = await getConfigByGroup('watermark')
    const items = res.data || res || []
    config = {}
    items.forEach(item => {
      config[item.configKey] = item.configValue
    })
    createWatermark()
  } catch (e) {
    // 登录页可能后端未启动，静默失败
    console.warn('[Watermark] 加载配置失败:', e)
  }
}

// ========== MutationObserver 防删除 ==========
function setupObserver() {
  if (!watermarkRef.value) return

  observer = new MutationObserver((mutations) => {
    let needRecreate = false
    for (const mutation of mutations) {
      if (mutation.type === 'childList' && mutation.removedNodes.length > 0) {
        needRecreate = true
        break
      }
      if (mutation.type === 'attributes' && mutation.target === watermarkRef.value) {
        needRecreate = true
        break
      }
    }

    if (needRecreate) {
      observer.disconnect()
      createWatermark()
      setupObserver()
    }
  })

  observer.observe(watermarkRef.value, {
    attributes: true,
    attributeFilter: ['style', 'class']
  })

  if (watermarkRef.value.parentNode) {
    observer.observe(watermarkRef.value.parentNode, {
      childList: true
    })
  }
}

// ========== 监听主题变化 ==========
watch(() => settingsStore.resolvedTheme, () => {
  createWatermark()
})

// ========== 生命周期 ==========
onMounted(() => {
  loadConfig().then(() => {
    setupObserver()
  })
})

onUnmounted(() => {
  if (observer) observer.disconnect()
})
</script>

<script>
export default { name: 'Watermark' }
</script>

<style lang="scss" scoped>
.watermark-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 9999;
  background-repeat: repeat;
}
</style>
